import React, { useState, useEffect, useMemo } from "react";
import { useAppSelector } from "../../store/hooks";
import {
  TextField,
  Autocomplete,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import "./SearchComponent.scss";
import { useGetUserDetailsQuery } from "../../../src/pages/add-admin/GetUsersDetailsAPISlice";

interface SearchResult {
  id: string;
  name: string;
  givenName: string;
  surName: string;
}

interface Users {
  userPrincipalName: string;
  displayName: string;
  givenName: string;
  surname: string;
}

interface SearchComponentProps {
  onSelection: (
    id: string,
    name: string,
    givenName: string,
    surName: string
  ) => void;
}

const SearchComponent: React.FC<SearchComponentProps> = ({ onSelection }) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<SearchResult[]>([]);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);

  const handleOnChange = (value: string) => {
    setInputValue(value);
    if (value.length < 4) {
      setOptions([]);
    } else {
      setCount(count + 1);
    }
  };

  const handleSearchClick = () => {
    if (inputValue.length < 4) {
      setOptions([]);
      setOpen(false);
    } else {
      setOpen(true);
    }
  };

  const accessToken = useAppSelector(
    (state) => state.adminAuth.user?.accessToken!
  );
  const { data, error, isLoading } = useGetUserDetailsQuery(
    {
      inputValue,
      accessToken,
      count,
    },
    { skip: inputValue.length < 4 }
  );

  // Use useMemo to format data for the table
  const formatDataForTable = useMemo(() => {
    return (
      data &&
      data.azureAdUsers.map(
        ({ displayName, userPrincipalName, givenName, surname }: Users) => ({
          id: displayName,
          name: userPrincipalName,
          givenName: givenName,
          surName: surname,
        })
      )
    );
  }, [data]);

  useEffect(() => {
    setOptions(formatDataForTable || []);
  }, [formatDataForTable]);

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      getOptionLabel={(options) => options.name}
      onBlur={() => setOptions([])}
      popupIcon={null}
      onChange={(event, value) => {
        if (value) {
          // Pass the selected id and name to the parent component
          onSelection(value.id, value.name, value.givenName, value.surName);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label=""
          className="userSearch"
          variant="outlined"
          onChange={(e) => handleOnChange(e.target.value)}
          placeholder="Search user by username or email id"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <IconButton onClick={handleSearchClick}>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
};

export default SearchComponent;
